/*  ========================================================================
    # Components - Subscription
    ========================================================================  */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../hooks/useTranslation';
import useLocalization from '../hooks/useLocalization';
import fetchLeporelloApi from '../helpers/fetchLeporelloApi';

const Subscription = ({ name, performances, categories, url }) => {
  const _ = useTranslation();
  const performancesContent = useRef(null);
  const [showPerformances, setShowPerformances] = useState(false);
  const [loadPerformances, setLoadPerformances] = useState(false);
  const [days, setDays] = useState([]);

  // Icons
  const local = useLocalization();
  const iconPlus = `${local('svgSprite')}#plus`;
  const iconMinus = `${local('svgSprite')}#minus`;

  const hasDiscountPrice = categories.some(cat => cat.discount_price);

  const getItemDateFormatted = date => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      year: '2-digit',
      month: '2-digit'
    });
  };

  // Load Performances after accordion open
  const setHeightContent = (content, contentHeight, action) => {
    // Init
    if (action === 'init') {
      content.style.height = '0px';
    }

    // Show
    if (action === 'show') {
      content.style.height = contentHeight;
      setTimeout(() => {
        content.style.height = 'auto';
      }, 200);
    }

    // Hide
    if (action === 'hide') {
      content.style.height = contentHeight;
      setTimeout(() => {
        content.style.height = '0px';
      }, 10);
    }
  };

  const handleHeightContent = () => {
    const content = performancesContent.current;

    if (content) {
      let contentHeight = `${performancesContent.current.clientHeight}px`;
      setHeightContent(content, contentHeight, 'init');

      if (showPerformances) {
        setHeightContent(content, contentHeight, 'show');
      } else {
        setHeightContent(content, contentHeight, 'hide');
      }

      window.addEventListener('resize', () => {
        if (showPerformances) {
          contentHeight = `${content.clientHeight}px`;
          content.style.height = 'auto';
        } else {
          contentHeight = `${content.clientHeight}px`;
          content.style.height = '0px';
        }
      });
    }
  };

  useEffect(() => {
    handleHeightContent();

    if (performances && showPerformances && !loadPerformances) {
      setLoadPerformances(true);
      const fetchData = async () => {
        const [data] = await fetchLeporelloApi(
          null,
          'ITEMS',
          'performance',
          null,
          new Date(),
          null,
          -1,
          0,
          {
            performancesInclude: performances
          }
        );
        setDays(data);
      };
      fetchData();
    }
  }, [showPerformances]);

  return (
    <div className='c-subscription'>
      <h3 className='c-subscription__title'>{name}</h3>
      <p className='c-subscription__description'>
        <a href={url} aria-label={name} className='c-button u-margin-top--sm'>
          {_('subscription_more_info_button_text')}
        </a>
      </p>
      <div className='c-subscription__categories'>
        <div className='c-subscription__categories-entries'>
          <p
            className='c-label c-label--badge'
            style={{
              '--label-badge-color': '#000000',
              '--label-badge-background': '#ffffff'
            }}
            aria-label={_('subscriptions_price_description', { title: name })}
          >
            {_('subscriptions_lable_price')}
          </p>
          {categories.map((cat, index) => (
            <p key={index}>
              <span
                key={index}
                className='c-label c-label--badge'
                style={{
                  '--label-badge-color': '#000000',
                  '--label-badge-background': cat.category_color
                }}
              >
                {cat.price}
              </span>
            </p>
          ))}
        </div>
        {hasDiscountPrice && (
          <div className='c-subscription__categories-entries'>
            <p
              className='c-label c-label--badge'
              style={{
                '--label-badge-color': '#000000',
                '--label-badge-background': '#ffffff'
              }}
              aria-label={_('subscriptions_discount_price_description', { title: name })}
            >
              {_('subscriptions_lable_discount_price')}
            </p>
            {categories.map((cat, index) => (
              <p key={index}>
                {cat.discount_price ? (
                  <span
                    key={index + '_discount'}
                    className='c-label c-label--badge'
                    style={{
                      '--label-badge-color': '#000000',
                      '--label-badge-background': cat.category_color
                    }}
                  >
                    {cat.discount_price}
                  </span>
                ) : null}
              </p>
            ))}
          </div>
        )}
      </div>
      {!performances ? null : (
        <div
          className={`c-subscription__performances ${showPerformances ? 'c-subscription__performances--open' : ''
            }`}
        >
          <button
            type='button'
            className='c-subscription__performances-header'
            aria-label={_('subscriptions_accrodion_title')}
            onClick={() => {
              setShowPerformances(!showPerformances);
            }}
          >
            <h4>{_('subscriptions_accrodion_title')}</h4>
            <div className='c-subscription__performances-icon'>
              <svg viewBox='0 0 200 200' className='c-subscription__performances-icon-plus'>
                <use href={iconPlus}></use>
              </svg>
              <svg viewBox='0 0 200 200' className='c-subscription__performances-icon-minus'>
                <use href={iconMinus}></use>
              </svg>
            </div>
          </button>
          <div className='c-subscription__performances-content' ref={performancesContent}>
            {days.length < 1 ? (
              <ul className='c-subscription__performances-list u-list-unstyled c-subscription__performances-list--skeleton'>
                {[...Array(3)].map((value, index) => (
                  <li className='c-subscription__performances-list-item' key={index}>
                    <p className='c-subscription__performances-list-item-date' />
                    <p className='c-subscription__performances-list-item-title' />
                    <p className='c-subscription__performances-list-item-time' />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className='c-subscription__performances-list u-list-unstyled c-subscription__performances-list'>
                {days.map((day, index) =>
                  day.items.map((item, itemIndex) => (
                    <li
                      className='c-subscription__performances-list-item'
                      key={`${index}_${itemIndex}`}
                    >
                      <p className='c-subscription__performances-list-item-date'>
                        {getItemDateFormatted(day.date)}
                      </p>
                      <p className='c-subscription__performances-list-item-title'>{item.title}</p>
                      <p className='c-subscription__performances-list-item-time'>
                        {_('subscriptions_accrodion_time', { time: item.startTime })}
                      </p>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Subscription.propTypes = {
  name: PropTypes.string,
  performances: PropTypes.array,
  categories: PropTypes.array,
  url: PropTypes.string
};

export default Subscription;
